import Image from "next/image";
import { useIntl } from "react-intl";
import { SetStateAction, useEffect, useState } from "react";
import classNames from "classnames";
import dayjs, { Dayjs } from "dayjs";
import { capitalize } from "@/utils/formatName";
import { RoundTripStartIcon } from "@/public/icons/RoundTripStartIcon";
// import { RoundTripEndIcon } from "@/public/icons/RoundTripEndIcon";
import { ChevronRight } from "@/public/icons/ChevronRight";
import { addBasePathToStrapiMedia, StrapiImage } from "@/utils/strapi";
import { InvoiceData } from "../../AccountB2B";
import { Invoices } from "./invoices/Invoices";
import styles from "./Contract.module.scss";

export type ContractProps = {
  invoiceData: InvoiceData;
  startDate: Dayjs;
  endDate: Dayjs;
  itemsChecked: string[];
  setItemsChecked: React.Dispatch<SetStateAction<string[]>>;
  images: StrapiImage[];
};

export const Contract = ({
  invoiceData,
  startDate,
  endDate,
  itemsChecked,
  setItemsChecked,
  images,
}: ContractProps) => {
  const intl = useIntl();

  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [invoicesCount, setInvoicesCount] = useState<number>(0);
  const [globalBalance, setGlobalBalance] = useState<number>();
  const [isContractBalanced, setIsContractBalanced] = useState<boolean>(true);

  const getContractBalance = () => {
    let total = 0;
    let totalUnpaid = 0;

    invoiceData.invoices.map((invoice) => {
      if (
        dayjs(invoice.invoiceDate).isAfter(startDate) &&
        dayjs(invoice.invoiceDate).isBefore(endDate)
      ) {
        if (!invoice.isBalanced && invoice.balance > 0) {
          totalUnpaid += invoice.balance;
        }
        if (
          (invoice.isBalanced || invoice.balance < 0) &&
          invoice.taxeIncludedPrice > 0
        ) {
          total += invoice.taxeIncludedPrice;
        }
      }
    });

    total = totalUnpaid > 0 ? totalUnpaid : total;

    setGlobalBalance(total);
    setIsContractBalanced(totalUnpaid <= 0);
  };

  const getInvoicesCount = (): number => {
    const nb = invoiceData.invoices.filter(
      (invoice) =>
        dayjs(invoice.invoiceDate).isAfter(startDate) &&
        dayjs(invoice.invoiceDate).isBefore(endDate)
    ).length;

    setInvoicesCount(nb);

    return nb;
  };

  useEffect(() => {
    getContractBalance();
    const nb = getInvoicesCount();

    if (nb > 0) {
      setShowDetails(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceData.invoices, startDate, endDate]);

  return (
    <>
      {invoiceData.contract && invoicesCount > 0 && (
        <div className={styles.content}>
          <div className={styles.contract}>
            <div className={styles.header}>
              <div className={styles.id}>
                N°{invoiceData.contract.documentId}
              </div>
              <div className={styles.agencyStart}>
                <RoundTripStartIcon size={24} color={"#062e52"} />
                {invoiceData.contract.pickupAgencyName}
              </div>
              {/* <div className={styles.agencyEnd}>
                <RoundTripEndIcon size={24} color={"#062e52"} />
              </div> */}
            </div>
            <div className={styles.infos}>
              <div className={styles.picture}>
                <Image
                  src={addBasePathToStrapiMedia(images[0])}
                  alt={images[0].alternativeText ?? ""}
                  layout="fill"
                  objectFit="contain"
                />
              </div>
              <div className={styles.vehicle}>
                <div className={styles.category}>
                  {intl.formatMessage(
                    { id: "categoryValue" },
                    {
                      cat: invoiceData.contract.category?.id,
                      label: invoiceData.contract.category?.vehicleLabel,
                    }
                  )}
                </div>
                <div className={styles.denomination}>
                  <div className={styles.plate}>
                    {invoiceData.contract.plateNumber}
                  </div>
                  <div className={styles.brand}></div>
                </div>
              </div>
              <div className={styles.drivers}>
                <div className={styles.label}>
                  {intl.formatMessage({ id: "drivers" }, { nb: 1 })}
                </div>
                <div className={styles.driver}>
                  <span className={styles.civility}>
                    {invoiceData.contract.mainDriverCivility}
                  </span>
                  <span className={styles.name}>
                    {capitalize(invoiceData.contract.mainDriverFirstName)}
                  </span>
                  <span className={styles.name}>
                    {capitalize(invoiceData.contract.mainDriverLastName)}
                  </span>
                </div>
              </div>
              <div
                className={classNames(styles.status, {
                  [styles.unpaid]: !isContractBalanced,
                })}
              >
                {intl.formatMessage({
                  id: isContractBalanced ? "paid" : "unpaid",
                })}
              </div>
              <div
                className={classNames(styles.balance, {
                  [styles.unpaid]: !isContractBalanced,
                })}
              >
                {globalBalance?.toFixed(2).replace(/[.,]00$/, "")} €
              </div>
              <div
                className={classNames(styles.seeDetails, {
                  [styles.active]: showDetails,
                })}
                onClick={() => setShowDetails(!showDetails)}
              >
                <ChevronRight size={"24"} />
              </div>
            </div>
          </div>
          {showDetails && (
            <Invoices
              invoices={invoiceData.invoices}
              startDate={startDate}
              endDate={endDate}
              itemsChecked={itemsChecked}
              setItemsChecked={setItemsChecked}
            />
          )}
        </div>
      )}
    </>
  );
};
